import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── POS Shift Management ────────────────────────────────────
 * Cash register shift lifecycle: open, track, close, reconcile.
 *
 * Features:
 *  - Open new shift with opening cash float
 *  - Real-time shift summary (sales, payments, payouts)
 *  - Close shift with cash count and discrepancy check
 *  - Shift history with reconciliation data
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { posShiftRepo } from '../../../lib/db'
import type { POSShift } from '@repo/entity-pos'
import {
  SHIFT_STATUS_LABELS, SHIFT_STATUS_COLORS,
} from '@repo/entity-pos'
import { POSShiftService } from '@repo/entity-pos'
import {
  Clock, Plus, RefreshCw, DollarSign, ShoppingBag,
  Receipt, AlertTriangle, CheckCircle, XCircle,
  TrendingUp, Wallet, ArrowUpDown,
} from 'lucide-react'

export function POSShiftsPage() {
  const [shifts, setShifts] = useState<POSShift[]>([])
  const [loading, setLoading] = useState(true)
  const [showOpenShift, setShowOpenShift] = useState(false)
  const [showCloseShift, setShowCloseShift] = useState<POSShift | null>(null)
  const [openingCash, setOpeningCash] = useState('500')
  const [actualCash, setActualCash] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await posShiftRepo.findMany({
        page: 1,
        pageSize: 50,
        sort: [{ field: 'createdAt', direction: 'desc' }],
      })
      if ('items' in result) setShifts(result.items as POSShift[])
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const activeShift = shifts.find((s) => s.status === 'open')

  const handleOpenShift = async () => {
    try {
      const now = new Date()
      const seq = (shifts.length + 1).toString().padStart(2, '0')
      const shift: POSShift = {
        id: crypto.randomUUID(),
        tenantId: 'default',
        shiftNumber: POSShiftService.generateShiftNumber(shifts.length + 1),
        shiftDate: now.toISOString().slice(0, 10),
        openTime: now.toTimeString().slice(0, 8),
        openedBy: 'admin',
        openedByName: 'Administrator',
        openingCash: parseFloat(openingCash) || 0,
        expectedCash: parseFloat(openingCash) || 0,
        totalSales: 0,
        transactionCount: 0,
        payouts: [],
        totalPayouts: 0,
        voidCount: 0,
        refundCount: 0,
        status: 'open',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        version: 1,
        createdBy: 'admin',
        updatedBy: 'admin',
      }
      await posShiftRepo.create(shift)
      setShowOpenShift(false)
      setOpeningCash('500')
      load()
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  const handleCloseShift = async () => {
    if (!showCloseShift) return
    const cash = parseFloat(actualCash)
    if (isNaN(cash)) {
      alert('Please enter actual cash counted.')
      return
    }

    const expected = POSShiftService.computeExpectedCash(
      showCloseShift.openingCash,
      showCloseShift.paymentBreakdown || {},
      showCloseShift.totalPayouts,
    )

    const discrepancy = POSShiftService.computeDiscrepancy(expected, cash)

    try {
      const now = new Date()
      await posShiftRepo.update(showCloseShift.id, {
        status: 'closed',
        closeTime: now.toTimeString().slice(0, 8),
        closedBy: 'admin',
        closedByName: 'Administrator',
        expectedCash: expected,
        actualCash: cash,
        cashDiscrepancy: discrepancy,
        version: showCloseShift.version,
      } as any)
      setShowCloseShift(null)
      setActualCash('')
      load()
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  const handleAddPayout = async (shift: POSShift, reason: string, amount: number) => {
    try {
      const payouts = [
        ...shift.payouts,
        { reason, amount, timestamp: Date.now() },
      ]
      await posShiftRepo.update(shift.id, {
        payouts,
        totalPayouts: shift.totalPayouts + amount,
        version: shift.version,
      } as any)
      load()
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shift Management</h1>
          <p className="text-sm text-gray-500">Cash register operations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} icon={<RefreshCw className="h-4 w-4" />}>Refresh</Button>
          {!activeShift && (
            <Button onClick={() => setShowOpenShift(true)} icon={<Plus className="h-4 w-4" />}>Open Shift</Button>
          )}
          <Link to="/pos/register">
            <Button variant="outline" icon={<ShoppingBag className="h-4 w-4" />}>POS Terminal</Button>
          </Link>
        </div>
      </div>

      {/* Active Shift Card */}
      {activeShift && (
        <div className="rounded-2xl border-2 border-green-400 bg-green-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-500 p-2">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Active Shift: {activeShift.shiftNumber}</h2>
                <p className="text-sm text-gray-600">
                  Opened {activeShift.shiftDate} at {activeShift.openTime} by {activeShift.openedByName}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowCloseShift(activeShift)} variant="outline">
                Close Shift
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <ShiftStat icon={<Wallet className="h-5 w-5 text-blue-600" />} label="Opening Cash" value={`₱${activeShift.openingCash.toFixed(2)}`} />
            <ShiftStat icon={<ShoppingBag className="h-5 w-5 text-green-600" />} label="Transactions" value={activeShift.transactionCount} />
            <ShiftStat icon={<DollarSign className="h-5 w-5 text-purple-600" />} label="Total Sales" value={`₱${activeShift.totalSales.toFixed(2)}`} />
            <ShiftStat icon={<Receipt className="h-5 w-5 text-yellow-600" />} label="Payouts" value={`₱${activeShift.totalPayouts.toFixed(2)}`} />
          </div>

          {/* Payouts */}
          {activeShift.payouts && activeShift.payouts.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-gray-500 uppercase">Payouts</p>
              <div className="space-y-1">
                {activeShift.payouts.map((p, i) => (
                  <div key={i} className="flex justify-between rounded bg-white/50 px-3 py-1 text-sm">
                    <span className="text-gray-700">{p.reason}</span>
                    <span className="font-medium text-red-600">-₱{p.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Payout */}
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddPayout(activeShift, 'Petty cash', 100)}
            >
              + Petty Cash ₱100
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAddPayout(activeShift, 'Supplies', 200)}
            >
              + Supplies ₱200
            </Button>
          </div>
        </div>
      )}

      {!activeShift && (
        <Card>
          <div className="p-8 text-center">
            <Clock className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p className="text-gray-500">No active shift</p>
            <p className="text-sm text-gray-400 mb-4">Open a new shift to begin processing sales</p>
            <Button onClick={() => setShowOpenShift(true)} icon={<Plus className="h-4 w-4" />}>Open Shift</Button>
          </div>
        </Card>
      )}

      {/* Shift History */}
      <Card>
        <CardHeader title="Shift History" description={`${shifts.length} shifts`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Shift #</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Open → Close</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Opened By</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Sales</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Opening</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Expected</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Actual</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Discrepancy</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : shifts.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No shifts recorded</td></tr>
              ) : (
                shifts.map((shift) => (
                  <tr key={shift.id} className={`border-b hover:bg-gray-50 ${shift.status === 'open' ? 'bg-green-50/50' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs font-medium">{shift.shiftNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{shift.shiftDate}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      <span>{shift.openTime}</span>
                      {shift.closeTime && <span> → {shift.closeTime}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{shift.openedByName}</td>
                    <td className="px-4 py-3 text-right font-medium">₱{shift.totalSales.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">₱{shift.openingCash.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{shift.status === 'closed' ? `₱${(shift.expectedCash || 0).toFixed(2)}` : '-'}</td>
                    <td className="px-4 py-3 text-right">{shift.actualCash != null ? `₱${shift.actualCash.toFixed(2)}` : '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {shift.cashDiscrepancy != null ? (
                        <span className={`font-medium ${shift.cashDiscrepancy === 0 ? 'text-green-600' : shift.cashDiscrepancy > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {shift.cashDiscrepancy > 0 ? '+' : ''}{shift.cashDiscrepancy.toFixed(2)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge color={SHIFT_STATUS_COLORS[shift.status]} size="sm">
                        {SHIFT_STATUS_LABELS[shift.status]}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Open Shift Modal */}
      {showOpenShift && (
        <Modal open={showOpenShift} onClose={() => setShowOpenShift(false)} title="Open New Shift">
          <div className="space-y-4 p-4">
            <p className="text-sm text-gray-600">
              Opening a shift begins a new cash register session. All sales processed will be recorded under this shift.
            </p>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Opening Cash Float (₱)</label>
              <Input
                type="number"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                placeholder="500"
                className="text-lg"
              />
              <p className="mt-1 text-xs text-gray-400">Amount of cash in the register drawer at the start of the shift.</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowOpenShift(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleOpenShift} className="flex-1" icon={<Plus className="h-4 w-4" />}>Open Shift</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Close Shift Modal */}
      {showCloseShift && (
        <Modal open={!!showCloseShift} onClose={() => setShowCloseShift(null)} title="Close Shift">
          <div className="space-y-4 p-4">
            <div className="rounded-lg bg-gray-50 p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Shift</span>
                <span className="font-medium">{showCloseShift.shiftNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Opening Cash</span>
                <span>₱{showCloseShift.openingCash.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total Sales</span>
                <span>₱{showCloseShift.totalSales.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Transactions</span>
                <span>{showCloseShift.transactionCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payouts</span>
                <span>₱{showCloseShift.totalPayouts.toFixed(2)}</span>
              </div>
              {showCloseShift.paymentBreakdown && (
                <>
                  <hr />
                  {Object.entries(showCloseShift.paymentBreakdown).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-gray-600">
                      <span>{k}</span>
                      <span>₱{(v as number).toFixed(2)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Actual Cash Counted (₱) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                value={actualCash}
                onChange={(e) => setActualCash(e.target.value)}
                placeholder="Count cash in drawer..."
                className="text-lg"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCloseShift(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleCloseShift} className="flex-1" icon={<CheckCircle className="h-4 w-4" />}>
                Confirm & Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function ShiftStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-white/80 p-3">
      <div className="flex items-center gap-2 mb-1">{icon}<p className="text-xs text-gray-600">{label}</p></div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  )
}
